import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import PageArrival from '../components/PageArrival';
import { Rocket as A1, Flame as A2, Trophy as A3, CheckSquare as A4, Clock as A5, Zap as A6 } from 'lucide-react';

import { AppLayout, LoadingSpinner } from '../components/Layout';
import { CheckCircle2, Circle, ArrowRight, Rocket, Sparkles, Lock } from 'lucide-react';
import RadialReadinessMeter from '../components/launch/RadialReadinessMeter';
import JourneyMap from '../components/launch/JourneyMap';
import LaunchModeCard from '../components/launch/LaunchModeCard';
import UnlockModal from '../components/launch/UnlockModal';
import BadgeShelf from '../components/launch/BadgeShelf';
import LaunchPackViewer from '../components/launch/LaunchPackViewer';
import PlanNextActions from '../components/plan/PlanNextActions';
import { AffordabilityPicker } from '../components/launch/AffordabilityPicker';
import PatriciaPageGuide from '../components/PatriciaPageGuide';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  core_profile: 'Core Profile', pathway_selection: 'Pathway Selection',
  skills_barriers: 'Skills & Interests', parent_alignment: 'Parent Alignment',
  financial_awareness: 'Financial Awareness', boosters: 'Boosters',
};

export default function LaunchModePage() {
  const [arrived, setArrived] = useState(false);
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUnlock, setShowUnlock] = useState(false);

  const load = () => {
    axios.get(`${API}/launch/readiness`, getAuthHeader ? getAuthHeader() : {})
      .then((r) => {
        setData(r.data);
        if (r.data.show_celebration || r.data.newly_unlocked) setShowUnlock(true);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeUnlock = () => {
    setShowUnlock(false);
    axios.post(`${API}/launch/celebrate-seen`, {}, getAuthHeader ? getAuthHeader() : {}).catch(() => {});
  };

  if (loading) return <AppLayout>
      <div className="mb-4"><PatriciaPageGuide pageId="launch" /></div><div className="flex justify-center py-20"><LoadingSpinner /></div></AppLayout>;
  if (!data) return <AppLayout><div className="text-slate-300 py-20 text-center">Launch Mode is available for students.</div></AppLayout>;

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="launch-arrival"
        eyebrow="Launch Mode"
        title="Ninety days. Head down."
        subtitle="The final stretch, run like a sprint. A short daily list, a visible streak, and a countdown to the thing you are actually going for."
        accent="#f472b6"
        second="#fbbf24"
        ctaLabel="Enter launch mode"
        narration={"This is Launch Mode. When the plan is set, the only thing left is doing it, so this strips everything back to a short daily list, a streak you will not want to break, and a countdown to the date that matters. Small, relentless progress, and something to celebrate every time you close a day out."}
        facts={[
          { icon: A5, head: "Ninety days", sub: "A finish line close enough to feel" },
          { icon: A4, head: "Today only", sub: "A short list, not your whole life" },
          { icon: A2, head: "Streaks", sub: "Momentum you can see" }
        ]}
        modules={[
          { icon: A4, label: "Daily list", line: "Three things, chosen from your roadmap", hue: "#f472b6" },
          { icon: A2, label: "Streak", line: "Days in a row, protected", hue: "#fbbf24" },
          { icon: A5, label: "Countdown", line: "Days until the date that matters", hue: "#22d3ee" },
          { icon: A3, label: "Wins", line: "Every completion marked", hue: "#34d399" },
          { icon: A6, label: "Quick actions", line: "Two-minute jobs that clear the way", hue: "#a78bfa" },
          { icon: A1, label: "Launch check", line: "Whether you are actually ready to go", hue: "#60a5fa" }
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  return (
    <AppLayout>
      <UnlockModal open={showUnlock} onClose={closeUnlock} percent={data.percent} level={data.readiness_level} />

      <div className="max-w-6xl mx-auto p-2 sm:p-4 space-y-6" data-testid="launch-mode-page">
        {/* Header */}
        <div className="relative">
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-300 font-semibold" style={{ textShadow: '0 0 14px rgba(23,204,244,0.6)' }}>FuturePath Journey · Dashboard</p>
          <h1 className="font-outfit text-3xl sm:text-4xl font-bold tracking-tight bg-gradient-to-r from-white via-cyan-100 to-cyan-300 bg-clip-text text-transparent" style={{ filter: 'drop-shadow(0 0 22px rgba(23,204,244,0.28))' }}>
            Your path to Launch Mode
          </h1>
          <p className="text-slate-300 text-sm mt-1">Everything you need to complete your journey, track progress, earn badges, and unlock your Launch Pack.</p>
        </div>

        {/* Meter + Launch Card */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="glass-card p-6 flex flex-col items-center justify-center">
            <RadialReadinessMeter percent={data.percent} level={data.readiness_level} />
            <p className="text-slate-300 text-xs mt-4 text-center">
              {data.required_completed}/{data.required_total} required steps complete
            </p>
          </div>
          <div className="lg:col-span-2">
            <LaunchModeCard data={data} onActivate={() => navigate('/app/launch')} />
          </div>
        </div>

        {/* Launch → living execution plan */}
        <PlanNextActions />

        {/* Journey Map */}
        {/* Your Journey, the dashboard centerpiece */}
        <div className="glass-card p-6" style={{ boxShadow: '0 0 40px rgba(23,204,244,0.12), 0 8px 32px rgba(0,0,0,0.35)' }}>
          <div className="flex items-center gap-2 mb-5">
            <span className="w-2 h-2 rounded-full bg-cyan-300" style={{ boxShadow: '0 0 10px #17CCF4' }} />
            <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-cyan-200" style={{ textShadow: '0 0 12px rgba(23,204,244,0.5)' }}>Your Journey</h2>
          </div>
          <JourneyMap journey={data.journey} />
        </div>

        {/* Requirements + Badges */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 glass-card p-6">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-300 mb-4">Unlock Requirements</h2>
            <div className="space-y-2">
              {data.requirements.map((r) => (
                <div key={r.key} className={`p-3 rounded-xl border ${r.done ? 'border-cyan-400/20 bg-cyan-500/5' : 'border-slate-700 bg-slate-900/40'}`} data-testid={`req-${r.key}`}>
                  <div className="flex items-center gap-3">
                    {r.done ? <CheckCircle2 className="w-5 h-5 text-cyan-300 flex-shrink-0" /> : <Circle className="w-5 h-5 text-slate-300 flex-shrink-0" />}
                    <span className={`text-sm flex-1 ${r.done ? 'text-slate-300' : 'text-white'}`}>{r.label}</span>
                    {!r.done && !r.inline && (
                      <button onClick={() => navigate(r.href)} className="text-xs font-semibold text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1 flex-shrink-0" data-testid={`req-cta-${r.key}`}>
                        {r.cta} <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  {r.inline === 'affordability' && (
                    <AffordabilityPicker done={r.done} onSaved={load} />
                  )}
                </div>
              ))}
            </div>

            {/* Category breakdown */}
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-300 mt-6 mb-3">Readiness breakdown</h3>
            <div className="space-y-2.5">
              {Object.entries(data.category_scores).map(([k, v]) => (
                <div key={k}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-slate-300">{CATEGORY_LABELS[k] || k}</span>
                    <span className="font-mono text-slate-300">{Math.round(v * 100)}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-900 overflow-hidden">
                    <motion.div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500" initial={{ width: 0 }} animate={{ width: `${Math.round(v * 100)}%` }} transition={{ duration: 0.9 }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="glass-card p-6">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-300 mb-4">Badges</h2>
              <BadgeShelf badges={data.badges} />
            </div>

            {/* Launch Pack teaser (locked only) */}
            {!data.unlocked && (
            <div className="rounded-2xl border p-6 border-slate-800 bg-slate-900/50" data-testid="launch-pack-teaser">
              <div className="flex items-center gap-2 mb-2">
                <Lock className="w-5 h-5 text-slate-300" />
                <h2 className="text-base font-semibold text-white">FuturePath Launch Pack</h2>
              </div>
              <p className="text-sm text-slate-300">
                Unlock Launch Mode to generate your personalized Launch Pack with NextMove AI.
              </p>
              <p className="mt-3 text-xs text-cyan-300/70">Coming when you reach Launch Ready</p>
            </div>
            )}
          </div>
        </div>

        {/* FuturePath Launch Pack, full AI-generated action layer (unlocked) */}
        {data.unlocked && (
          <div className="rounded-2xl border border-white/10 bg-slate-900/40 backdrop-blur-xl p-5 sm:p-6">
            <LaunchPackViewer />
          </div>
        )}
      </div>
    </AppLayout>
  );
}
